import type { InvoiceDetail } from "@stb/api-contract"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { useState } from "react"

import config from "@/config"

import {
  InvoiceList,
  InvoiceListError,
  InvoiceListSkeleton,
} from "../components/invoice-list/invoice-list"
import { PaymentDialog } from "../components/payment-dialog/payment-dialog"
import { loadInvoiceList, loadInvoiceUsage, recordPaymentAction } from "../rpc"
import { sydneyToday } from "../status"

export const Route = createFileRoute("/_app-shell/invoices")({
  loader: async () => {
    const [invoices, usage] = await Promise.all([loadInvoiceList({ data: {} }), loadInvoiceUsage()])
    return { invoices, usage, today: sydneyToday(new Date()) }
  },
  head: () => ({
    meta: [
      { title: `Invoices - ${config.core.projectName}` },
      { name: "robots", content: "noindex" },
    ],
  }),
  pendingComponent: InvoiceListSkeleton,
  errorComponent: ({ error }) => {
    const router = useRouter()
    return (
      <InvoiceListError
        message={error.message || "Invoices could not be loaded."}
        onRetry={() => router.invalidate()}
      />
    )
  },
  component: InvoicesRoute,
})

function InvoicesRoute() {
  const { invoices, usage, today } = Route.useLoaderData()
  const router = useRouter()
  const recordPayment = useServerFn(recordPaymentAction)
  const [paying, setPaying] = useState<InvoiceDetail | null>(null)

  return (
    <>
      <InvoiceList invoices={invoices} usage={usage} today={today} onRecordPayment={setPaying} />
      {paying && (
        <PaymentDialog
          invoice={paying}
          today={today}
          open
          onOpenChange={(next) => !next && setPaying(null)}
          onSubmit={(request) => recordPayment({ data: { invoiceId: paying.id, request } })}
          onDone={() => {
            setPaying(null)
            // re-fetch so the list never shows stale amounts or statuses
            void router.invalidate()
          }}
        />
      )}
    </>
  )
}
